import { TFunction } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import _ from 'underscore';

import {
  BasicCompanyInformation,
  LicenseInformation,
} from '../../../../../typings/api/skymap/rest/v0/company';
import { sessionStorageUtils } from '../../../utils/session-storage-utils';
import { isDefined } from '../../../utils/variables';
import { useExpiredLicenseGuard } from '../../hooks/use-expired-license-guard';
import { useOptionalUserState } from '../../state/user-state/user-state';
import { ThemeManager } from '../../themes/theme-manager';
import { Button } from '../button/button';
import { DataTable, DataTableColumns, DataTableSortType } from '../data-table/data-table';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import { PendingTransferIndicator } from '../pending-transfer-indicator/pending-transfer-indicator';
import { useGetCompaniesQuery } from '../projects-views/use-get-companies-query';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import type { PendingProjectTransferListItem } from './pending-project-transfers/pending-project-transfer-types';
import { usePendingProjectTransfersQuery } from './pending-project-transfers/use-pending-project-transfers-query';

const pageSize = 15;

export interface CompanyTableView {
  id: string;
  accountName: string;
  licenseText: string;
  name: string;
  adminCount: number;
  projectCount: number;
  [key: string]: string | number | undefined;
}

function getBaseColumns(
  t: TFunction,
  isOrganizationAdmin: boolean,
): DataTableColumns<CompanyTableView> {
  if (isOrganizationAdmin) {
    return {
      name: {
        sortableBy: { type: DataTableSortType.STRING },
        title: t('name', { ns: 'common' }),
        unfilterable: true,
      },
      accountName: {
        sortableBy: { type: DataTableSortType.STRING },
        title: t('columns.account', { ns: 'companies' }),
        unfilterable: true,
      },
      licenseText: {
        sortableBy: { type: DataTableSortType.STRING },
        title: t('menu.licenses.title', { ns: 'navigation' }),
        unfilterable: true,
      },
      adminCount: {
        sortableBy: { type: DataTableSortType.NUMBER },
        title: t('columns.adminCount', { ns: 'companies' }),
        shrink: true,
        unfilterable: true,
      },
      projectCount: {
        sortableBy: { type: DataTableSortType.NUMBER },
        title: t('columns.projectCount', { ns: 'companies' }),
        shrink: true,
        unfilterable: true,
      },
    };
  }

  return {
    name: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('name', { ns: 'common' }),
      unfilterable: true,
    },
    licenseText: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('menu.licenses.title', { ns: 'navigation' }),
      unfilterable: true,
    },
    adminCount: {
      sortableBy: { type: DataTableSortType.NUMBER },
      title: t('columns.adminCount', { ns: 'companies' }),
      shrink: true,
      unfilterable: true,
    },
    projectCount: {
      sortableBy: { type: DataTableSortType.NUMBER },
      title: t('columns.projectCount', { ns: 'companies' }),
      shrink: true,
      unfilterable: true,
    },
  };
}

/**
 * Returns a few temporary rows to show underneath the overlay loader while the data is fetching.
 * Should ideally be the same amount of rows
 */
function getPlaceholderItems(): CompanyTableView[] {
  return _.times(pageSize, () => {
    return {
      id: '',
      name: '---',
      accountName: '---',
      licenseText: '',
      adminCount: 0,
      projectCount: 0,
    };
  });
}

function summarizeLicenses(licenses: LicenseInformation[], t: TFunction): string {
  const hasActiveDesignLicense = licenses.some((x) => x.type === 'DESIGN' && x.active > 0);

  // Note: Don't display any information about demo if there are active licenses.
  const licensesToDisplay = hasActiveDesignLicense
    ? licenses.filter((x) => x.type !== 'DESIGN_DEMO')
    : licenses;

  const getLicenseCountText = (license: LicenseInformation): string => {
    if (license.active + license.expired === 0) {
      return '';
    }

    const parts = [];
    if (license.active > 0) {
      parts.push(t('licenses.activeCount', { ns: 'companies', count: license.active }));
    }

    if (license.expired > 0) {
      parts.push(t('licenses.expiredCount', { ns: 'companies', count: license.expired }));
    }

    return `(${parts.join(', ')})`;
  };

  return licensesToDisplay
    .map((x) => `${x.type} ${getLicenseCountText(x)}`)
    .join(', ')
    .trim();
}

const createTableView = (companies: BasicCompanyInformation[], t: TFunction): CompanyTableView[] =>
  companies.map((company) => ({
    name: company.name,
    id: company.id,
    accountName: company.accountName ?? '',
    licenseText: isDefined(company.licenses) ? summarizeLicenses(company.licenses, t) : '',
    adminCount: company.adminCount,
    projectCount: company.projectCount,
  }));

const searchColumns = ['name', 'licenseText', 'accountName'] as const;

function filterItems(items: CompanyTableView[], searchText: string) {
  return items.filter((item) => {
    const searchValue = searchText.trim().toLowerCase();
    return searchColumns.some((x) => (item[x] ?? '').toLowerCase().includes(searchValue));
  });
}

/**
 * Hook that preserves text in search during the user session.
 */
function useCompanySerachFilter() {
  const [searchText, setSearchText] = useState(
    sessionStorageUtils.getUserItemOrDefault('companyListSearchText', ''),
  );

  useEffect(() => {
    sessionStorageUtils.setUserItem('companyListSearchText', searchText);
  }, [searchText]);

  return { searchText, setSearchText };
}

export const CompanyList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const userState = useOptionalUserState();
  const isOrganizationAdmin = userState?.isOrganizationAdmin ?? false;
  const navigate = useNavigate();

  const { isCompanyLicenseValid, showExpiredDialog, renderExpiredLicenseDialog } =
    useExpiredLicenseGuard();
  const { companies, buildErrorList, hasError, isLoading } = useGetCompaniesQuery();
  const { data: pendingTransfersData } = usePendingProjectTransfersQuery();
  const companyIdsWithPending = useMemo(() => {
    const list: PendingProjectTransferListItem[] = pendingTransfersData?.data ?? [];
    return new Set(list.filter((i) => i.status === 'PENDING').map((i) => i.companyId));
  }, [pendingTransfersData]);
  const pendingTransferTooltip = t('pendingProjectTransfers.attentionTooltip', { ns: 'companies' });

  const [tableView, setTableView] = useState<CompanyTableView[]>(getPlaceholderItems());
  const { searchText, setSearchText } = useCompanySerachFilter();
  const showRegisterCompanyButton = !ThemeManager.instance.isMobaTheme();

  const columns = useMemo<DataTableColumns<CompanyTableView>>(() => {
    const base = getBaseColumns(t, isOrganizationAdmin);
    return {
      ...base,
      pendingTransfer: {
        render: (item) =>
          companyIdsWithPending.has(item.id) ? (
            <PendingTransferIndicator
              style={{ color: 'var(--color-brand-dark, #0d6efd)' }}
              title={pendingTransferTooltip}
            />
          ) : null,
        shrink: true,
        title: '',
        unfilterable: true,
        width: '2rem',
      },
    };
  }, [companyIdsWithPending, isOrganizationAdmin, pendingTransferTooltip, t]);

  useEffect(() => {
    if (isDefined(companies)) {
      const items = filterItems(createTableView(companies, t), searchText);
      setTableView(items);
    }
  }, [companies, searchText, theme, t]);

  if (hasError()) {
    return (
      <>
        {buildErrorList({
          infoBoxProps: {
            width: 'fit-content',
          },
        })}
      </>
    );
  }

  return (
    <>
      {renderExpiredLicenseDialog()}
      <OverlayLoader visible={isLoading}>
        <Stack alignItems="baseline" direction="row" justifyContent="space-between" spacing={0.5}>
          <SearchFilterContainer direction="row" spacing={0.5}>
            <TextBox
              placeholder={t('search', { ns: 'common' })}
              value={searchText}
              width={250}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </SearchFilterContainer>

          {showRegisterCompanyButton && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => void navigate('/registercompany')}
            >
              {t('registerNewCompany', { ns: 'companies' })}
            </Button>
          )}
        </Stack>

        <DataTable
          columns={columns}
          fixedLayout={false}
          items={tableView}
          pageSize={pageSize}
          tableStyle={{
            rowCell: {
              fontSize: '14px',
            },
            headerCell: {
              fontSize: '14px',
            },
          }}
          onRowClicked={(x) => {
            if (!isOrganizationAdmin && !isCompanyLicenseValid(x.id)) {
              showExpiredDialog(x.id);
              return;
            }

            void navigate(`/companies/${x.id}`);
          }}
        />
      </OverlayLoader>
    </>
  );
};

const SearchFilterContainer = styled(Stack)`
  width: 450px;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;
